import React from 'react';
import { toast } from 'sonner';
import { FileText, Image as ImageIcon, Loader2, Trash2, UploadCloud, Eye } from 'lucide-react';
import { adminApi, getAdminUser } from '../../lib/adminAuth';
import { formatDate } from '../../lib/dateUtils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.heic';
const MAX = 10 * 1024 * 1024;
const kb = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function ProofRow({ po, proof, onDeleted, canDelete }) {
  const [busy, setBusy] = React.useState(false);
  const base = `/api/admin/accounting/purchase-orders/${po._id}/payment-proofs/${proof.id}`;
  const open = async () => {
    try {
      const r = await adminApi.get(base, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Could not open file'); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${proof.filename}?`)) return;
    setBusy(true);
    try {
      await adminApi.delete(base);
      onDeleted(proof.id);
      toast.success('Proof deleted');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
    finally { setBusy(false); }
  };
  const Icon = (proof.content_type || '').startsWith('image/') ? ImageIcon : FileText;
  return (
    <li className="flex items-center gap-3 p-2.5 rounded-xl border border-slate-200/70 bg-white hover:border-fuchsia-200 transition-colors" data-testid={`payment-proof-item-${proof.id}`}>
      <div className="w-9 h-9 rounded-lg bg-fuchsia-50 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-fuchsia-600" /></div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-[#1a1a2e] truncate" title={proof.filename}>{proof.filename}</p>
        <p className="text-[11px] text-slate-500">{kb(proof.size || 0)} · {proof.uploaded_by_name || 'Unknown'} · {proof.uploaded_at ? formatDate(proof.uploaded_at) : ''}</p>
      </div>
      <button onClick={open} className="p-1.5 rounded-lg hover:bg-slate-100" title="View" data-testid={`payment-proof-view-${proof.id}`}><Eye className="w-4 h-4 text-slate-600" /></button>
      {canDelete && <button onClick={remove} disabled={busy} className="p-1.5 rounded-lg hover:bg-red-50 disabled:opacity-50" title="Delete" data-testid={`payment-proof-delete-${proof.id}`}><Trash2 className="w-4 h-4 text-red-600" /></button>}
    </li>
  );
}

export default function POPaymentProofsDialog({ po, onClose, onChange }) {
  const [uploading, setUploading] = React.useState(false);
  const [drag, setDrag] = React.useState(false);
  const inputRef = React.useRef(null);
  const proofs = po?.payment_proofs || [];
  const canDelete = ['admin', 'superadmin'].includes(getAdminUser()?.role);

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    let next = [...proofs];
    for (const f of list) {
      if (f.size > MAX) { toast.error(`${f.name} is larger than 10 MB`); continue; }
      const fd = new FormData();
      fd.append('file', f);
      try {
        const r = await adminApi.post(`/api/admin/accounting/purchase-orders/${po._id}/payment-proofs`, fd, { timeout: 120000 });
        next = [...next, r.data];
        toast.success(`${f.name} uploaded`);
      } catch (e) { toast.error(e?.response?.data?.detail || `${f.name} failed to upload`); }
    }
    onChange(po._id, next);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <Dialog open={!!po} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="payment-proof-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1a1a2e]">Payment proof · {po?.po_number}</DialogTitle>
          <DialogDescription>Receipts or bank screenshots for {po?.vendor_name || 'this vendor'}. PDF or image, up to 10 MB each.</DialogDescription>
        </DialogHeader>
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
          onClick={() => !uploading && inputRef.current?.click()}
          className={`cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-colors ${drag ? 'border-fuchsia-500 bg-fuchsia-50' : 'border-slate-200 hover:border-fuchsia-300 hover:bg-fuchsia-50/40'}`}
          data-testid="payment-proof-dropzone"
        >
          {uploading ? <Loader2 className="w-6 h-6 mx-auto text-fuchsia-600 animate-spin" /> : <UploadCloud className="w-6 h-6 mx-auto text-fuchsia-600" />}
          <p className="text-sm font-medium text-[#1a1a2e] mt-2">{uploading ? 'Uploading…' : 'Drop files here or click to choose'}</p>
          <p className="text-xs text-slate-500 mt-0.5">PDF, PNG, JPG, WEBP, HEIC</p>
          <input ref={inputRef} type="file" accept={ACCEPT} multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="payment-proof-file-input" />
        </div>
        <ul className="space-y-2 max-h-64 overflow-y-auto" data-testid="payment-proof-list">
          {proofs.length === 0 ? (
            <li className="text-xs text-slate-500 text-center py-3">No proof attached yet</li>
          ) : proofs.map((p) => (
            <ProofRow key={p.id} po={po} proof={p} canDelete={canDelete} onDeleted={(id) => onChange(po._id, proofs.filter((x) => x.id !== id))} />
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
